import { Card, CardContent } from "@/components/ui/card";
import { 
  Heart, 
  Sparkles, 
  Cake,
  ArrowRight,
  ChevronDown
} from "lucide-react";
import Letterhead from "@/components/Letterhead";

// Component to display a filled form visually
const FilledForm = ({ formData, occasionIcon: Icon, occasionColor }) => {
  // Convert form data to displayable fields
  const getFields = () => {
    const fields = [];
    
    if (formData.occasion) fields.push({ label: "Anledning", value: formData.occasion });
    if (formData.recipient) fields.push({ label: "Talen er til", value: formData.recipient });
    if (formData.speaker) fields.push({ label: "Hvem holder talen", value: formData.speaker });
    if (formData.tone) fields.push({ label: "Tone", value: formData.tone });
    if (formData.length) fields.push({ label: "Længde", value: formData.length });
    if (formData.child_age) fields.push({ label: "Barnets alder", value: formData.child_age });
    if (formData.hobbies) fields.push({ label: "Interesser og hobbyer", value: formData.hobbies });
    if (formData.how_they_met) fields.push({ label: "Hvordan mødtes parret", value: formData.how_they_met });
    if (formData.anecdote1) fields.push({ label: "Anekdote 1", value: formData.anecdote1 });
    if (formData.anecdote2) fields.push({ label: "Anekdote 2", value: formData.anecdote2 });
    if (formData.anecdote3) fields.push({ label: "Anekdote 3", value: formData.anecdote3 });
    if (formData.what_makes_them_great) fields.push({ label: "Hvad gør dem gode sammen", value: formData.what_makes_them_great });
    if (formData.proud_of) fields.push({ label: "Hvad du er stolt af", value: formData.proud_of });
    if (formData.personality) fields.push({ label: "Personens egenskaber", value: formData.personality });
    if (formData.milestones) fields.push({ label: "Livsmilepæle", value: formData.milestones });
    if (formData.wishes) fields.push({ label: "Ønsker/budskab", value: formData.wishes });
    
    return fields;
  };

  const fields = getFields();

  return (
    <Card className="bg-white border-border/40 h-full">
      <CardContent className="p-4 md:p-6">
        {/* Form header */}
        <div className="flex items-center gap-3 mb-4 pb-3 border-b border-border/40">
          <div className={`w-10 h-10 rounded-full ${occasionColor} flex items-center justify-center`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
          <div>
            <h4 className="font-heading text-base md:text-lg font-bold text-primary">
              Udfyldt formular
            </h4>
            <p className="text-xs text-muted-foreground">Brugerens input</p>
          </div>
        </div>
        
        {/* Form fields */}
        <div className="space-y-3 max-h-[400px] md:max-h-[500px] overflow-y-auto pr-2">
          {fields.map((field, index) => (
            <div key={index} className="group">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {field.label}
              </label>
              <div className="mt-1 p-2 md:p-3 bg-muted/30 rounded-lg border border-border/30">
                <p className="text-xs md:text-sm text-foreground leading-relaxed">
                  {field.value}
                </p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Main example card showing form → speech transformation
const ExampleCard = ({ 
  formData, 
  speech, 
  title,
  subtitle,
  occasionIcon, 
  occasionColor,
  expanded,
  onToggle,
  testId
}) => {
  return (
    <div className="space-y-4" data-testid={testId}>
      {/* Mobile: Stacked layout */}
      <div className="block lg:hidden space-y-4">
        {/* Form */}
        <FilledForm 
          formData={formData} 
          occasionIcon={occasionIcon} 
          occasionColor={occasionColor}
        />
        
        {/* Arrow */}
        <div className="flex items-center justify-center py-2">
          <div className="flex items-center gap-2 text-secondary font-medium">
            <span className="text-sm">blev til</span>
            <ChevronDown className="h-5 w-5" />
          </div>
        </div>
        
        {/* Speech on letterhead */}
        <div className={expanded ? "" : "max-h-[400px] overflow-hidden relative"}>
          <Letterhead title={title} subtitle={subtitle}>
            {speech}
          </Letterhead>
          {!expanded && (
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
          )}
        </div>
      </div>

      {/* Desktop: Side by side */}
      <div className="hidden lg:grid lg:grid-cols-2 gap-6">
        {/* Form on left */}
        <FilledForm 
          formData={formData} 
          occasionIcon={occasionIcon} 
          occasionColor={occasionColor}
        />
        
        {/* Arrow in middle (absolutely positioned) */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 hidden lg:flex">
          <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center shadow-lg">
            <ArrowRight className="h-6 w-6 text-primary" />
          </div>
        </div>
        
        {/* Speech on letterhead on right */}
        <div className={expanded ? "" : "max-h-[550px] overflow-hidden relative"}>
          <Letterhead title={title} subtitle={subtitle}>
            {speech}
          </Letterhead>
          {!expanded && (
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
          )}
        </div>
      </div>
      
      {/* Toggle button */}
      <button
        onClick={onToggle}
        className="w-full py-3 text-sm font-medium text-primary hover:text-primary/80 transition-colors flex items-center justify-center gap-2"
      >
        {expanded ? "Vis mindre" : "Læs hele talen"}
        <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>
    </div>
  );
};

export { ExampleCard, FilledForm };
export default ExampleCard;
